/**
 * Phase-2 API Helpers
 *
 * Thin wrappers over the shared axios `api` client for the Phase-2 modules.
 * Each function returns the unwrapped `data` payload (`response.data.data`).
 *
 * Additive — does not modify the existing `api.ts` client or Phase-1 services.
 *
 * @author Civilhealth Development Team
 */
import api from './api';
import { AxiosError } from 'axios';
import type {
  OfficerMedicalReport,
  OfficerMedicalReportRequest,
  InsuranceCompany,
  OfficerInsurance,
  OfficerInsuranceRequest,
  HospitalAdmission,
  AdmissionRequest,
  PrescriptionDocument,
  InvestigationRequest,
  InvestigationCreateRequest,
  Laboratory,
  LaboratoryRequest,
  LabTestCatalog,
  LabTestCatalogRequest,
  BloodTestMaster,
  BloodTestBooking,
  BloodTestBookingRequest,
  MedicalDocument,
  MedicalDocumentRequest,
  PagedResponse,
} from '../types/phase2';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8082/api';

/** Convert a backend-relative file URL (e.g. /uploads/...) to an absolute URL for preview/download.
 *  Always returns a string (empty when no URL) so it can be passed directly to `href`. */
export const toAbsoluteFileUrl = (url?: string): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = API_BASE_URL.replace(/\/api\/?$/i, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const errorMessage = (error: unknown, fallback: string) => {
  const err = error as AxiosError<{ message: string }>;
  return err.response?.data?.message || fallback;
};

// ==================== Officer Medical Report (Module 2) ====================
export const OfficerMedicalReportApi = {
  upload: async (file: File, meta: OfficerMedicalReportRequest): Promise<OfficerMedicalReport> => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null) form.append(k, String(v));
    });
    const res = await api.post('/officer-medical-reports', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },
  getById: async (id: number) => (await api.get(`/officer-medical-reports/${id}`)).data.data as OfficerMedicalReport,
  getByOfficer: async (officerId: number) =>
    (await api.get(`/officer-medical-reports/officer/${officerId}`)).data.data as OfficerMedicalReport[],
  verify: async (id: number) => (await api.post(`/officer-medical-reports/${id}/verify`)).data.data as OfficerMedicalReport,
  delete: async (id: number) => (await api.delete(`/officer-medical-reports/${id}`)).data.data,
};

// ==================== Insurance (Module 3) ====================
export const InsuranceApi = {
  listCompanies: async () => (await api.get('/insurance-companies/active')).data.data as InsuranceCompany[],
  listByOfficer: async (officerId: number) =>
    (await api.get(`/officers/${officerId}/insurance`)).data.data as OfficerInsurance[],
  listAll: async (params?: Record<string, unknown>) =>
    (await api.get('/officer-insurance', { params })).data.data as PagedResponse<OfficerInsurance>,
  create: async (req: OfficerInsuranceRequest) =>
    (await api.post('/officer-insurance', req)).data.data as OfficerInsurance,
  verify: async (id: number, approved: boolean, rejectionReason?: string) =>
    (await api.post(`/officer-insurance/${id}/verify`, { insuranceId: id, approved, rejectionReason })).data.data as OfficerInsurance,
  delete: async (id: number) => (await api.delete(`/officer-insurance/${id}`)).data.data,
};

// ==================== IPD Hospital Admission (Module 4) ====================
export const AdmissionApi = {
  create: async (req: AdmissionRequest) => (await api.post('/admissions', req)).data.data as HospitalAdmission,
  getById: async (id: number) => (await api.get(`/admissions/${id}`)).data.data as HospitalAdmission,
  listByOfficer: async (officerId: number) =>
    (await api.get(`/admissions/officer/${officerId}`)).data.data as HospitalAdmission[],
  listAll: async (params?: Record<string, unknown>) =>
    (await api.get('/admissions', { params })).data.data as PagedResponse<HospitalAdmission>,
  review: async (id: number) => (await api.post(`/admissions/${id}/review`)).data.data as HospitalAdmission,
  approve: async (id: number) => (await api.post(`/admissions/${id}/approve`)).data.data as HospitalAdmission,
  reject: async (id: number, reason: string) =>
    (await api.post(`/admissions/${id}/reject`, { rejectionReason: reason })).data.data as HospitalAdmission,
  admit: async (id: number) => (await api.post(`/admissions/${id}/admit`)).data.data as HospitalAdmission,
  startTreatment: async (id: number) =>
    (await api.post(`/admissions/${id}/start-treatment`)).data.data as HospitalAdmission,
  discharge: async (id: number) => (await api.post(`/admissions/${id}/discharge`)).data.data as HospitalAdmission,
  close: async (id: number) => (await api.post(`/admissions/${id}/close`)).data.data as HospitalAdmission,
  getTimeline: async (id: number) =>
    (await api.get(`/admissions/${id}/timeline`)).data.data as HospitalAdmission['timeline'],
  uploadDocument: async (id: number, file: File, documentType: string, documentName: string, description?: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentType', documentType);
    form.append('documentName', documentName);
    if (description) form.append('description', description);
    return (await api.post(`/admissions/${id}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data;
  },
  getDocuments: async (id: number) =>
    (await api.get(`/admissions/${id}/documents`)).data.data as HospitalAdmission['documents'],
  deleteDocument: async (id: number, docId: number) =>
    (await api.delete(`/admissions/${id}/documents/${docId}`)).data.data,
};

// ==================== Prescription Document (Module 5) ====================
export const PrescriptionDocumentApi = {
  upload: async (file: File, meta: { officerId: number; patientName?: string; doctorName?: string; hospitalName?: string; prescriptionDate?: string; validUntil?: string; notes?: string; familyMemberId?: number }) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null) form.append(k, String(v));
    });
    return (await api.post('/prescription-documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as PrescriptionDocument;
  },
  getById: async (id: number) => (await api.get(`/prescription-documents/${id}`)).data.data as PrescriptionDocument,
  listByOfficer: async (officerId: number) =>
    (await api.get(`/prescription-documents/officer/${officerId}`)).data.data as PrescriptionDocument[],
  search: async (params: Record<string, unknown>) =>
    (await api.get('/prescription-documents/search', { params })).data.data as PagedResponse<PrescriptionDocument>,
  createVersion: async (id: number, file: File, meta: { documentTitle?: string; notes?: string }) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null) form.append(k, String(v));
    });
    return (await api.post(`/prescription-documents/${id}/versions`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as PrescriptionDocument;
  },
  getVersions: async (id: number) =>
    (await api.get(`/prescription-documents/${id}/versions`)).data.data as PrescriptionDocument[],
  delete: async (id: number) => (await api.delete(`/prescription-documents/${id}`)).data.data,
};

// ==================== Investigation (Module 6) ====================
export const InvestigationApi = {
  create: async (req: InvestigationCreateRequest) =>
    (await api.post('/investigations', req)).data.data as InvestigationRequest,
  getById: async (id: number) => (await api.get(`/investigations/${id}`)).data.data as InvestigationRequest,
  listByOfficer: async (officerId: number) =>
    (await api.get(`/investigations/officer/${officerId}`)).data.data as InvestigationRequest[],
  listAll: async (params?: Record<string, unknown>) =>
    (await api.get('/investigations', { params })).data.data as PagedResponse<InvestigationRequest>,
  approve: async (id: number) => (await api.post(`/investigations/${id}/approve`)).data.data as InvestigationRequest,
  assignLab: async (id: number, labId: number) =>
    (await api.post(`/investigations/${id}/assign`, { labId })).data.data as InvestigationRequest,
  markInProgress: async (id: number) => (await api.post(`/investigations/${id}/in-progress`)).data.data as InvestigationRequest,
  uploadReport: async (id: number, file: File, results?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (results) form.append('results', results);
    return (await api.post(`/investigations/${id}/report-file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as InvestigationRequest;
  },
  complete: async (id: number) => (await api.post(`/investigations/${id}/complete`)).data.data as InvestigationRequest,
  reject: async (id: number, reason: string) =>
    (await api.post(`/investigations/${id}/reject`, { rejectionReason: reason })).data.data as InvestigationRequest,
};

// ==================== Laboratory + Test Catalog (Module 7) ====================
export const LaboratoryApi = {
  list: async (params?: Record<string, unknown>) =>
    (await api.get('/laboratories', { params })).data.data as PagedResponse<Laboratory>,
  register: async (req: LaboratoryRequest) =>
    (await api.post('/laboratories', req)).data.data as Laboratory,
  getActive: async () => (await api.get('/laboratories/active')).data.data as Laboratory[],
  getById: async (id: number) => (await api.get(`/laboratories/${id}`)).data.data as Laboratory,
  approve: async (id: number) => (await api.post(`/laboratories/${id}/approve`)).data.data as Laboratory,
  activate: async (id: number) => (await api.post(`/laboratories/${id}/activate`)).data.data as Laboratory,
  reject: async (id: number, reason: string) =>
    (await api.post(`/laboratories/${id}/reject`, undefined, { params: { reason } })).data.data as Laboratory,
  // Test catalog
  listCatalog: async (labId: number, activeOnly = false) =>
    (await api.get(`/laboratories/${labId}/catalog`, { params: { activeOnly } })).data.data as LabTestCatalog[],
  addCatalogTest: async (labId: number, req: LabTestCatalogRequest) =>
    (await api.post(`/laboratories/${labId}/catalog`, req)).data.data as LabTestCatalog,
  updateCatalogTest: async (testId: number, req: LabTestCatalogRequest) =>
    (await api.put(`/laboratories/catalog/${testId}`, req)).data.data as LabTestCatalog,
  toggleCatalogTest: async (testId: number, active: boolean) =>
    (await api.post(`/laboratories/catalog/${testId}/toggle`, undefined, { params: { active } })).data.data as LabTestCatalog,
  deleteCatalogTest: async (testId: number) => (await api.delete(`/laboratories/catalog/${testId}`)).data.data,
};

// ==================== Blood Test Booking (Module 8) ====================
export const BloodTestApi = {
  listAvailableTests: async () => (await api.get('/blood-tests/available-tests')).data.data as BloodTestMaster[],
  book: async (req: BloodTestBookingRequest) => (await api.post('/blood-tests', req)).data.data as BloodTestBooking,
  getById: async (id: number) => (await api.get(`/blood-tests/${id}`)).data.data as BloodTestBooking,
  listByOfficer: async (officerId: number) =>
    (await api.get(`/blood-tests/officer/${officerId}`)).data.data as PagedResponse<BloodTestBooking>,
  listAll: async (params?: Record<string, unknown>) =>
    (await api.get('/blood-tests', { params })).data.data as PagedResponse<BloodTestBooking>,
  confirm: async (id: number) => (await api.post(`/blood-tests/${id}/confirm`)).data.data as BloodTestBooking,
  markSampleCollected: async (id: number) => (await api.post(`/blood-tests/${id}/sample-collected`)).data.data as BloodTestBooking,
  markProcessing: async (id: number) => (await api.post(`/blood-tests/${id}/processing`)).data.data as BloodTestBooking,
  uploadReport: async (id: number, file: File, notes?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (notes) form.append('notes', notes);
    return (await api.post(`/blood-tests/${id}/report-file`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as BloodTestBooking;
  },
  complete: async (id: number) => (await api.post(`/blood-tests/${id}/complete`)).data.data as BloodTestBooking,
  cancel: async (id: number, reason: string) =>
    (await api.post(`/blood-tests/${id}/cancel`, { cancellationReason: reason })).data.data as BloodTestBooking,
};

// ==================== Medical Document Repository (Module 9) ====================
export const MedicalDocumentApi = {
  upload: async (file: File, meta: MedicalDocumentRequest) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null) form.append(k, String(v));
    });
    return (await api.post('/medical-documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as MedicalDocument;
  },
  getById: async (id: number) => (await api.get(`/medical-documents/${id}`)).data.data as MedicalDocument,
  listByOfficer: async (officerId: number, params?: Record<string, unknown>) =>
    (await api.get(`/medical-documents/officer/${officerId}`, { params })).data.data as PagedResponse<MedicalDocument>,
  listAll: async (params?: Record<string, unknown>) =>
    (await api.get('/medical-documents', { params })).data.data as PagedResponse<MedicalDocument>,
  update: async (id: number, req: Partial<MedicalDocumentRequest>) =>
    (await api.put(`/medical-documents/${id}`, req)).data.data as MedicalDocument,
  verify: async (id: number) => (await api.post(`/medical-documents/${id}/verify`)).data.data as MedicalDocument,
  createVersion: async (id: number, file: File, meta: { documentTitle?: string; documentDate?: string; expiryDate?: string; notes?: string }) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== undefined && v !== null) form.append(k, String(v));
    });
    return (await api.post(`/medical-documents/${id}/versions`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data.data as MedicalDocument;
  },
  getVersions: async (id: number) => (await api.get(`/medical-documents/${id}/versions`)).data.data as MedicalDocument[],
  delete: async (id: number) => (await api.delete(`/medical-documents/${id}`)).data.data,
};

// ==================== Health Timeline (Module 10) ====================
export const TimelineApi = {
  listByOfficer: async (officerId: number, params?: Record<string, unknown>) =>
    (await api.get(`/timeline/officer/${officerId}`, { params })).data.data,
  getRecent: async (officerId: number, limit = 5) =>
    (await api.get(`/timeline/officer/${officerId}/recent`, { params: { limit } })).data.data,
  getSummary: async (officerId: number) => (await api.get(`/timeline/officer/${officerId}/summary`)).data.data,
};

// ==================== Office + Office Staff (Module 1) ====================
export interface OfficeRow {
  id: number; officeName?: string; officeType?: string; city?: string; state?: string;
  district?: string; address?: string; contactNumber?: string; staffCount?: number; officerCount?: number; status?: string;
}
export interface OfficeStaffRow {
  id: number; officeId?: number; userId?: number;
  // Real backend (OfficeStaffResponse) field names:
  username?: string; fullName?: string; mobile?: string; department?: string;
  officeName?: string; joiningDate?: string; isPrimaryOffice?: boolean;
  canCreateRequests?: boolean; canUploadDocuments?: boolean; canTrackRequests?: boolean;
  // Older alias fields (kept for compatibility):
  userName?: string; staffRole?: string; designation?: string; contactNumber?: string;
  email?: string; isActive?: boolean;
}

export const OfficeApi = {
  list: async (params?: Record<string, unknown>) => {
    const d = (await api.get('/offices', { params })).data.data;
    return (Array.isArray(d) ? d : (d?.content ?? [])) as OfficeRow[];
  },
  getById: async (id: number) => (await api.get(`/offices/${id}`)).data.data as OfficeRow,
};

export interface OfficeStaffRequest {
  userId: number; officeId: number; designation?: string; department?: string;
  joiningDate?: string; isPrimaryOffice?: boolean; canCreateRequests?: boolean;
  canUploadDocuments?: boolean; canTrackRequests?: boolean; notes?: string;
}
export interface OfficeStaffUpdateRequest {
  designation?: string; department?: string; joiningDate?: string; isPrimaryOffice?: boolean;
  canCreateRequests?: boolean; canUploadDocuments?: boolean; canTrackRequests?: boolean;
  notes?: string;
}

export interface AssignableUser {
  id: number; username: string; fullName: string; email?: string; mobile?: string; role?: string;
}

export const OfficeStaffApi = {
  listByOffice: async (officeId: number) =>
    (await api.get(`/office-staff/office/${officeId}`)).data.data as OfficeStaffRow[],
  listAssignableUsers: async () =>
    (await api.get('/office-staff/assignable-users')).data.data as AssignableUser[],
  assign: async (req: OfficeStaffRequest) =>
    (await api.post('/office-staff', req)).data.data as OfficeStaffRow,
  update: async (id: number, req: OfficeStaffUpdateRequest) =>
    (await api.put(`/office-staff/${id}`, req)).data.data as OfficeStaffRow,
  remove: async (id: number) => (await api.delete(`/office-staff/${id}`)).data.data,
};

export { errorMessage };